/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const K = globalThis, ee = K.ShadowRoot && (K.ShadyCSS === void 0 || K.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, te = Symbol(), se = /* @__PURE__ */ new WeakMap();
let Ee = class {
  constructor(e, t, r) {
    if (this._$cssResult$ = !0, r !== te)
      throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = e, this.t = t;
  }
  get styleSheet() {
    let e = this.o;
    const t = this.t;
    if (ee && e === void 0) {
      const r = t !== void 0 && t.length === 1;
      r && (e = se.get(t)), e === void 0 && ((this.o = e = new CSSStyleSheet()).replaceSync(this.cssText), r && se.set(t, e));
    }
    return e;
  }
  toString() {
    return this.cssText;
  }
};
const ke = (o) => new Ee(typeof o == "string" ? o : o + "", void 0, te), H = (o, ...e) => {
  const t = o.length === 1 ? o[0] : e.reduce((r, i, n) => r + ((s) => {
    if (s._$cssResult$ === !0)
      return s.cssText;
    if (typeof s == "number")
      return s;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + s + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(i) + o[n + 1], o[0]);
  return new Ee(t, o, te);
}, Me = (o, e) => {
  if (ee)
    o.adoptedStyleSheets = e.map((t) => t instanceof CSSStyleSheet ? t : t.styleSheet);
  else
    for (const t of e) {
      const r = document.createElement("style"), i = K.litNonce;
      i !== void 0 && r.setAttribute("nonce", i), r.textContent = t.cssText, o.appendChild(r);
    }
}, ne = ee ? (o) => o : (o) => o instanceof CSSStyleSheet ? ((e) => {
  let t = "";
  for (const r of e.cssRules)
    t += r.cssText;
  return ke(t);
})(o) : o;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: Ie, defineProperty: He, getOwnPropertyDescriptor: je, getOwnPropertyNames: Ve, getOwnPropertySymbols: ze, getPrototypeOf: Ke } = Object, A = globalThis, ae = A.trustedTypes, Fe = ae ? ae.emptyScript : "", q = A.reactiveElementPolyfillSupport, L = (o, e) => o, G = { toAttribute(o, e) {
  switch (e) {
    case Boolean:
      o = o ? Fe : null;
      break;
    case Object:
    case Array:
      o = o == null ? o : JSON.stringify(o);
  }
  return o;
}, fromAttribute(o, e) {
  let t = o;
  switch (e) {
    case Boolean:
      t = o !== null;
      break;
    case Number:
      t = o === null ? null : Number(o);
      break;
    case Object:
    case Array:
      try {
        t = JSON.parse(o);
      } catch {
        t = null;
      }
  }
  return t;
} }, re = (o, e) => !Ie(o, e), ce = { attribute: !0, type: String, converter: G, reflect: !1, hasChanged: re };
var ve, ye;
(ve = Symbol.metadata) != null || (Symbol.metadata = Symbol("metadata")), (ye = A.litPropertyMetadata) != null || (A.litPropertyMetadata = /* @__PURE__ */ new WeakMap());
class N extends HTMLElement {
  static addInitializer(e) {
    var t;
    this._$Ei(), ((t = this.l) != null ? t : this.l = []).push(e);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(e, t = ce) {
    if (t.state && (t.attribute = !1), this._$Ei(), this.elementProperties.set(e, t), !t.noAccessor) {
      const r = Symbol(), i = this.getPropertyDescriptor(e, r, t);
      i !== void 0 && He(this.prototype, e, i);
    }
  }
  static getPropertyDescriptor(e, t, r) {
    var s;
    const { get: i, set: n } = (s = je(this.prototype, e)) != null ? s : { get() {
      return this[t];
    }, set(c) {
      this[t] = c;
    } };
    return { get() {
      return i == null ? void 0 : i.call(this);
    }, set(c) {
      const a = i == null ? void 0 : i.call(this);
      n.call(this, c), this.requestUpdate(e, a, r);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(e) {
    var t;
    return (t = this.elementProperties.get(e)) != null ? t : ce;
  }
  static _$Ei() {
    if (this.hasOwnProperty(L("elementProperties")))
      return;
    const e = Ke(this);
    e.finalize(), e.l !== void 0 && (this.l = [...e.l]), this.elementProperties = new Map(e.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(L("finalized")))
      return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(L("properties"))) {
      const t = this.properties, r = [...Ve(t), ...ze(t)];
      for (const i of r)
        this.createProperty(i, t[i]);
    }
    const e = this[Symbol.metadata];
    if (e !== null) {
      const t = litPropertyMetadata.get(e);
      if (t !== void 0)
        for (const [r, i] of t)
          this.elementProperties.set(r, i);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [t, r] of this.elementProperties) {
      const i = this._$Eu(t, r);
      i !== void 0 && this._$Eh.set(i, t);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(e) {
    const t = [];
    if (Array.isArray(e)) {
      const r = new Set(e.flat(1 / 0).reverse());
      for (const i of r)
        t.unshift(ne(i));
    } else
      e !== void 0 && t.push(ne(e));
    return t;
  }
  static _$Eu(e, t) {
    const r = t.attribute;
    return r === !1 ? void 0 : typeof r == "string" ? r : typeof e == "string" ? e.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    var e;
    this._$ES = new Promise((t) => this.enableUpdating = t), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), (e = this.constructor.l) == null || e.forEach((t) => t(this));
  }
  addController(e) {
    var t, r;
    ((t = this._$EO) != null ? t : this._$EO = /* @__PURE__ */ new Set()).add(e), this.renderRoot !== void 0 && this.isConnected && ((r = e.hostConnected) == null || r.call(e));
  }
  removeController(e) {
    var t;
    (t = this._$EO) == null || t.delete(e);
  }
  _$E_() {
    const e = /* @__PURE__ */ new Map(), t = this.constructor.elementProperties;
    for (const r of t.keys())
      this.hasOwnProperty(r) && (e.set(r, this[r]), delete this[r]);
    e.size > 0 && (this._$Ep = e);
  }
  createRenderRoot() {
    var t;
    const e = (t = this.shadowRoot) != null ? t : this.attachShadow(this.constructor.shadowRootOptions);
    return Me(e, this.constructor.elementStyles), e;
  }
  connectedCallback() {
    var e, t;
    (e = this.renderRoot) != null || (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (t = this._$EO) == null || t.forEach((r) => {
      var i;
      return (i = r.hostConnected) == null ? void 0 : i.call(r);
    });
  }
  enableUpdating(e) {
  }
  disconnectedCallback() {
    var e;
    (e = this._$EO) == null || e.forEach((t) => {
      var r;
      return (r = t.hostDisconnected) == null ? void 0 : r.call(t);
    });
  }
  attributeChangedCallback(e, t, r) {
    this._$AK(e, r);
  }
  _$EC(e, t) {
    var n;
    const r = this.constructor.elementProperties.get(e), i = this.constructor._$Eu(e, r);
    if (i !== void 0 && r.reflect === !0) {
      const s = (((n = r.converter) == null ? void 0 : n.toAttribute) !== void 0 ? r.converter : G).toAttribute(t, r.type);
      this._$Em = e, s == null ? this.removeAttribute(i) : this.setAttribute(i, s), this._$Em = null;
    }
  }
  _$AK(e, t) {
    var n;
    const r = this.constructor, i = r._$Eh.get(e);
    if (i !== void 0 && this._$Em !== i) {
      const s = r.getPropertyOptions(i), c = typeof s.converter == "function" ? { fromAttribute: s.converter } : ((n = s.converter) == null ? void 0 : n.fromAttribute) !== void 0 ? s.converter : G;
      this._$Em = i, this[i] = c.fromAttribute(t, s.type), this._$Em = null;
    }
  }
  requestUpdate(e, t, r) {
    var i;
    if (e !== void 0) {
      if (r != null || (r = this.constructor.getPropertyOptions(e)), !((i = r.hasChanged) != null ? i : re)(this[e], t))
        return;
      this.P(e, t, r);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$ET());
  }
  P(e, t, r) {
    var i;
    this._$AL.has(e) || this._$AL.set(e, t), r.reflect === !0 && this._$Em !== e && ((i = this._$Ej) != null ? i : this._$Ej = /* @__PURE__ */ new Set()).add(e);
  }
  async _$ET() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (t) {
      Promise.reject(t);
    }
    const e = this.scheduleUpdate();
    return e != null && await e, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var r, i;
    if (!this.isUpdatePending)
      return;
    if (!this.hasUpdated) {
      if ((r = this.renderRoot) != null || (this.renderRoot = this.createRenderRoot()), this._$Ep) {
        for (const [s, c] of this._$Ep)
          this[s] = c;
        this._$Ep = void 0;
      }
      const n = this.constructor.elementProperties;
      if (n.size > 0)
        for (const [s, c] of n)
          c.wrapped !== !0 || this._$AL.has(s) || this[s] === void 0 || this.P(s, this[s], c);
    }
    let e = !1;
    const t = this._$AL;
    try {
      e = this.shouldUpdate(t), e ? (this.willUpdate(t), (i = this._$EO) == null || i.forEach((n) => {
        var s;
        return (s = n.hostUpdate) == null ? void 0 : s.call(n);
      }), this.update(t)) : this._$EU();
    } catch (n) {
      throw e = !1, this._$EU(), n;
    }
    e && this._$AE(t);
  }
  willUpdate(e) {
  }
  _$AE(e) {
    var t;
    (t = this._$EO) == null || t.forEach((r) => {
      var i;
      return (i = r.hostUpdated) == null ? void 0 : i.call(r);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(e)), this.updated(e);
  }
  _$EU() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(e) {
    return !0;
  }
  update(e) {
    this._$Ej && (this._$Ej = this._$Ej.forEach((t) => this._$EC(t, this[t]))), this._$EU();
  }
  updated(e) {
  }
  firstUpdated(e) {
  }
}
var $e;
N.elementStyles = [], N.shadowRootOptions = { mode: "open" }, N[L("elementProperties")] = /* @__PURE__ */ new Map(), N[L("finalized")] = /* @__PURE__ */ new Map(), q == null || q({ ReactiveElement: N }), (($e = A.reactiveElementVersions) != null ? $e : A.reactiveElementVersions = []).push("2.0.4");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const We = { attribute: !0, type: String, converter: G, reflect: !1, hasChanged: re }, Ge = (o = We, e, t) => {
  const { kind: r, metadata: i } = t;
  let n = globalThis.litPropertyMetadata.get(i);
  if (n === void 0 && globalThis.litPropertyMetadata.set(i, n = /* @__PURE__ */ new Map()), n.set(t.name, o), r === "accessor") {
    const { name: s } = t;
    return { set(c) {
      const a = e.get.call(this);
      e.set.call(this, c), this.requestUpdate(s, a, o);
    }, init(c) {
      return c !== void 0 && this.P(s, void 0, o), c;
    } };
  }
  if (r === "setter") {
    const { name: s } = t;
    return function(c) {
      const a = this[s];
      e.call(this, c), this.requestUpdate(s, a, o);
    };
  }
  throw Error("Unsupported decorator location: " + r);
};
function b(o) {
  return (e, t) => typeof t == "object" ? Ge(o, e, t) : ((r, i, n) => {
    const s = i.hasOwnProperty(n);
    return i.constructor.createProperty(n, s ? { ...r, wrapped: !0 } : r), s ? Object.getOwnPropertyDescriptor(i, n) : void 0;
  })(o, e, t);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function Ye(o) {
  return b({ ...o, state: !0, attribute: !1 });
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const U = globalThis, Y = U.trustedTypes, le = Y ? Y.createPolicy("lit-html", { createHTML: (o) => o }) : void 0, ie = "$lit$", y = `lit$${Math.random().toFixed(9).slice(2)}$`, oe = "?" + y, qe = `<${oe}>`, C = document, k = () => C.createComment(""), M = (o) => o === null || typeof o != "object" && typeof o != "function", Se = Array.isArray, Ce = (o) => Se(o) || typeof (o == null ? void 0 : o[Symbol.iterator]) == "function", X = `[ 	
\f\r]`, R = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, he = /-->/g, de = />/g, E = RegExp(`>|${X}(?:([^\\s"'>=/]+)(${X}*=${X}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), ue = /'/g, pe = /"/g, De = /^(?:script|style|textarea|title)$/i, Xe = (o) => (e, ...t) => ({ _$litType$: o, strings: e, values: t }), Ze = Xe(1), T = Symbol.for("lit-noChange"), u = Symbol.for("lit-nothing"), be = /* @__PURE__ */ new WeakMap(), S = C.createTreeWalker(C, 129);
function Ne(o, e) {
  if (!Array.isArray(o) || !o.hasOwnProperty("raw"))
    throw Error("invalid template strings array");
  return le !== void 0 ? le.createHTML(e) : e;
}
const Pe = (o, e) => {
  const t = o.length - 1, r = [];
  let i, n = e === 2 ? "<svg>" : "", s = R;
  for (let c = 0; c < t; c++) {
    const a = o[c];
    let l, d, h = -1, m = 0;
    for (; m < a.length && (s.lastIndex = m, d = s.exec(a), d !== null); )
      m = s.lastIndex, s === R ? d[1] === "!--" ? s = he : d[1] !== void 0 ? s = de : d[2] !== void 0 ? (De.test(d[2]) && (i = RegExp("</" + d[2], "g")), s = E) : d[3] !== void 0 && (s = E) : s === E ? d[0] === ">" ? (s = i != null ? i : R, h = -1) : d[1] === void 0 ? h = -2 : (h = s.lastIndex - d[2].length, l = d[1], s = d[3] === void 0 ? E : d[3] === '"' ? pe : ue) : s === pe || s === ue ? s = E : s === he || s === de ? s = R : (s = E, i = void 0);
    const $ = s === E && o[c + 1].startsWith("/>") ? " " : "";
    n += s === R ? a + qe : h >= 0 ? (r.push(l), a.slice(0, h) + ie + a.slice(h) + y + $) : a + y + (h === -2 ? c : $);
  }
  return [Ne(o, n + (o[t] || "<?>") + (e === 2 ? "</svg>" : "")), r];
};
class I {
  constructor({ strings: e, _$litType$: t }, r) {
    let i;
    this.parts = [];
    let n = 0, s = 0;
    const c = e.length - 1, a = this.parts, [l, d] = Pe(e, t);
    if (this.el = I.createElement(l, r), S.currentNode = this.el.content, t === 2) {
      const h = this.el.content.firstChild;
      h.replaceWith(...h.childNodes);
    }
    for (; (i = S.nextNode()) !== null && a.length < c; ) {
      if (i.nodeType === 1) {
        if (i.hasAttributes())
          for (const h of i.getAttributeNames())
            if (h.endsWith(ie)) {
              const m = d[s++], $ = i.getAttribute(h).split(y), V = /([.?@])?(.*)/.exec(m);
              a.push({ type: 1, index: n, name: V[2], strings: $, ctor: V[1] === "." ? Te : V[1] === "?" ? Oe : V[1] === "@" ? Re : j }), i.removeAttribute(h);
            } else
              h.startsWith(y) && (a.push({ type: 6, index: n }), i.removeAttribute(h));
        if (De.test(i.tagName)) {
          const h = i.textContent.split(y), m = h.length - 1;
          if (m > 0) {
            i.textContent = Y ? Y.emptyScript : "";
            for (let $ = 0; $ < m; $++)
              i.append(h[$], k()), S.nextNode(), a.push({ type: 2, index: ++n });
            i.append(h[m], k());
          }
        }
      } else if (i.nodeType === 8)
        if (i.data === oe)
          a.push({ type: 2, index: n });
        else {
          let h = -1;
          for (; (h = i.data.indexOf(y, h + 1)) !== -1; )
            a.push({ type: 7, index: n }), h += y.length - 1;
        }
      n++;
    }
  }
  static createElement(e, t) {
    const r = C.createElement("template");
    return r.innerHTML = e, r;
  }
}
function D(o, e, t = o, r) {
  var s, c, a;
  if (e === T)
    return e;
  let i = r !== void 0 ? (s = t._$Co) == null ? void 0 : s[r] : t._$Cl;
  const n = M(e) ? void 0 : e._$litDirective$;
  return (i == null ? void 0 : i.constructor) !== n && ((c = i == null ? void 0 : i._$AO) == null || c.call(i, !1), n === void 0 ? i = void 0 : (i = new n(o), i._$AT(o, t, r)), r !== void 0 ? ((a = t._$Co) != null ? a : t._$Co = [])[r] = i : t._$Cl = i), i !== void 0 && (e = D(o, i._$AS(o, e.values), i, r)), e;
}
class xe {
  constructor(e, t) {
    this._$AV = [], this._$AN = void 0, this._$AD = e, this._$AM = t;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(e) {
    var l;
    const { el: { content: t }, parts: r } = this._$AD, i = ((l = e == null ? void 0 : e.creationScope) != null ? l : C).importNode(t, !0);
    S.currentNode = i;
    let n = S.nextNode(), s = 0, c = 0, a = r[0];
    for (; a !== void 0; ) {
      if (s === a.index) {
        let d;
        a.type === 2 ? d = new O(n, n.nextSibling, this, e) : a.type === 1 ? d = new a.ctor(n, a.name, a.strings, this, e) : a.type === 6 && (d = new Le(n, this, e)), this._$AV.push(d), a = r[++c];
      }
      s !== (a == null ? void 0 : a.index) && (n = S.nextNode(), s++);
    }
    return S.currentNode = C, i;
  }
  p(e) {
    let t = 0;
    for (const r of this._$AV)
      r !== void 0 && (r.strings !== void 0 ? (r._$AI(e, r, t), t += r.strings.length - 2) : r._$AI(e[t])), t++;
  }
}
class O {
  get _$AU() {
    var e, t;
    return (t = (e = this._$AM) == null ? void 0 : e._$AU) != null ? t : this._$Cv;
  }
  constructor(e, t, r, i) {
    var n;
    this.type = 2, this._$AH = u, this._$AN = void 0, this._$AA = e, this._$AB = t, this._$AM = r, this.options = i, this._$Cv = (n = i == null ? void 0 : i.isConnected) != null ? n : !0;
  }
  get parentNode() {
    let e = this._$AA.parentNode;
    const t = this._$AM;
    return t !== void 0 && (e == null ? void 0 : e.nodeType) === 11 && (e = t.parentNode), e;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(e, t = this) {
    e = D(this, e, t), M(e) ? e === u || e == null || e === "" ? (this._$AH !== u && this._$AR(), this._$AH = u) : e !== this._$AH && e !== T && this._(e) : e._$litType$ !== void 0 ? this.$(e) : e.nodeType !== void 0 ? this.T(e) : Ce(e) ? this.k(e) : this._(e);
  }
  S(e) {
    return this._$AA.parentNode.insertBefore(e, this._$AB);
  }
  T(e) {
    this._$AH !== e && (this._$AR(), this._$AH = this.S(e));
  }
  _(e) {
    this._$AH !== u && M(this._$AH) ? this._$AA.nextSibling.data = e : this.T(C.createTextNode(e)), this._$AH = e;
  }
  $(e) {
    var n;
    const { values: t, _$litType$: r } = e, i = typeof r == "number" ? this._$AC(e) : (r.el === void 0 && (r.el = I.createElement(Ne(r.h, r.h[0]), this.options)), r);
    if (((n = this._$AH) == null ? void 0 : n._$AD) === i)
      this._$AH.p(t);
    else {
      const s = new xe(i, this), c = s.u(this.options);
      s.p(t), this.T(c), this._$AH = s;
    }
  }
  _$AC(e) {
    let t = be.get(e.strings);
    return t === void 0 && be.set(e.strings, t = new I(e)), t;
  }
  k(e) {
    Se(this._$AH) || (this._$AH = [], this._$AR());
    const t = this._$AH;
    let r, i = 0;
    for (const n of e)
      i === t.length ? t.push(r = new O(this.S(k()), this.S(k()), this, this.options)) : r = t[i], r._$AI(n), i++;
    i < t.length && (this._$AR(r && r._$AB.nextSibling, i), t.length = i);
  }
  _$AR(e = this._$AA.nextSibling, t) {
    var r;
    for ((r = this._$AP) == null ? void 0 : r.call(this, !1, !0, t); e && e !== this._$AB; ) {
      const i = e.nextSibling;
      e.remove(), e = i;
    }
  }
  setConnected(e) {
    var t;
    this._$AM === void 0 && (this._$Cv = e, (t = this._$AP) == null || t.call(this, e));
  }
}
class j {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(e, t, r, i, n) {
    this.type = 1, this._$AH = u, this._$AN = void 0, this.element = e, this.name = t, this._$AM = i, this.options = n, r.length > 2 || r[0] !== "" || r[1] !== "" ? (this._$AH = Array(r.length - 1).fill(new String()), this.strings = r) : this._$AH = u;
  }
  _$AI(e, t = this, r, i) {
    const n = this.strings;
    let s = !1;
    if (n === void 0)
      e = D(this, e, t, 0), s = !M(e) || e !== this._$AH && e !== T, s && (this._$AH = e);
    else {
      const c = e;
      let a, l;
      for (e = n[0], a = 0; a < n.length - 1; a++)
        l = D(this, c[r + a], t, a), l === T && (l = this._$AH[a]), s || (s = !M(l) || l !== this._$AH[a]), l === u ? e = u : e !== u && (e += (l != null ? l : "") + n[a + 1]), this._$AH[a] = l;
    }
    s && !i && this.j(e);
  }
  j(e) {
    e === u ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, e != null ? e : "");
  }
}
class Te extends j {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(e) {
    this.element[this.name] = e === u ? void 0 : e;
  }
}
class Oe extends j {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(e) {
    this.element.toggleAttribute(this.name, !!e && e !== u);
  }
}
class Re extends j {
  constructor(e, t, r, i, n) {
    super(e, t, r, i, n), this.type = 5;
  }
  _$AI(e, t = this) {
    var s;
    if ((e = (s = D(this, e, t, 0)) != null ? s : u) === T)
      return;
    const r = this._$AH, i = e === u && r !== u || e.capture !== r.capture || e.once !== r.once || e.passive !== r.passive, n = e !== u && (r === u || i);
    i && this.element.removeEventListener(this.name, this, r), n && this.element.addEventListener(this.name, this, e), this._$AH = e;
  }
  handleEvent(e) {
    var t, r;
    typeof this._$AH == "function" ? this._$AH.call((r = (t = this.options) == null ? void 0 : t.host) != null ? r : this.element, e) : this._$AH.handleEvent(e);
  }
}
class Le {
  constructor(e, t, r) {
    this.element = e, this.type = 6, this._$AN = void 0, this._$AM = t, this.options = r;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(e) {
    D(this, e);
  }
}
const pt = { P: ie, A: y, C: oe, M: 1, L: Pe, R: xe, D: Ce, V: D, I: O, H: j, N: Oe, U: Re, B: Te, F: Le }, Z = U.litHtmlPolyfillSupport;
var _e;
Z == null || Z(I, O), ((_e = U.litHtmlVersions) != null ? _e : U.litHtmlVersions = []).push("3.1.3");
const Je = (o, e, t) => {
  var n, s;
  const r = (n = t == null ? void 0 : t.renderBefore) != null ? n : e;
  let i = r._$litPart$;
  if (i === void 0) {
    const c = (s = t == null ? void 0 : t.renderBefore) != null ? s : null;
    r._$litPart$ = i = new O(e.insertBefore(k(), c), c, void 0, t != null ? t : {});
  }
  return i._$AI(o), i;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
class P extends N {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    var t, r;
    const e = super.createRenderRoot();
    return (r = (t = this.renderOptions).renderBefore) != null || (t.renderBefore = e.firstChild), e;
  }
  update(e) {
    const t = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(e), this._$Do = Je(t, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var e;
    super.connectedCallback(), (e = this._$Do) == null || e.setConnected(!0);
  }
  disconnectedCallback() {
    var e;
    super.disconnectedCallback(), (e = this._$Do) == null || e.setConnected(!1);
  }
  render() {
    return T;
  }
}
var we;
P._$litElement$ = !0, P.finalized = !0, (we = globalThis.litElementHydrateSupport) == null || we.call(globalThis, { LitElement: P });
const J = globalThis.litElementPolyfillSupport;
J == null || J({ LitElement: P });
var Ae;
((Ae = globalThis.litElementVersions) != null ? Ae : globalThis.litElementVersions = []).push("4.0.5");
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Ue = Symbol.for(""), Qe = (o) => {
  if ((o == null ? void 0 : o.r) === Ue)
    return o == null ? void 0 : o._$litStatic$;
}, Be = (o) => ({ _$litStatic$: o, r: Ue }), fe = /* @__PURE__ */ new Map(), et = (o) => (e, ...t) => {
  const r = t.length;
  let i, n;
  const s = [], c = [];
  let a, l = 0, d = !1;
  for (; l < r; ) {
    for (a = e[l]; l < r && (n = t[l], (i = Qe(n)) !== void 0); )
      a += i + e[++l], d = !0;
    l !== r && c.push(n), s.push(a), l++;
  }
  if (l === r && s.push(e[r]), d) {
    const h = s.join("$$lit$$");
    (e = fe.get(h)) === void 0 && (s.raw = s, fe.set(h, e = s)), t = c;
  }
  return o(e, ...t);
}, bt = et(Ze);
var F = /* @__PURE__ */ ((o) => (o.NO_SYNC = "no-sync", o))(F || {}), x = /* @__PURE__ */ ((o) => (o.VISIBLE = "visible", o.IDLE = "idle", o.INTERACTION = "interaction", o.MEDIA = "media", o.DEFINED = "defined", o))(x || {}), _ = /* @__PURE__ */ ((o) => (o.BLUR = "beacon-event-blur", o.CHANGE = "beacon-event-change", o.CLICK = "beacon-event-click", o.FOCUS = "beacon-event-focus", o.HIDE = "beacon-event-hide", o.INPUT = "beacon-event-input", o.KEYDOWN = "beacon-event-keydown", o.MOUSEDOWN = "beacon-event-mousedown", o.MOUSEENTER = "beacon-event-mouseenter", o.MOUSELEAVE = "beacon-event-mouseleave", o.RENDER = "beacon-event-render", o.SORT = "beacon-event-sort", o.DEFERRED_CHILD = "beacon-event-deferred-child", o.RENDER_ACTION_ZONE = "beacon-event-render-action-zone", o.RENDER_TABLE_CARDS = "beacon-event-render-table-cards", o.LANGUAGE_SELECT = "beacon-event-language-select", o.PLAY = "beacon-event-play", o.CLOSE = "beacon-event-close", o.OPEN = "beacon-event-open", o.NAV_LANGUAGE_SELECT = "beacon-event-language-selected", o.CLOSE_ACTIVE_NAV_ITEM = "beacon-event-close-active-nav-item", o.ELEMENT_READY = "beacon-event-element-ready", o.ACTIVE_NAV_ITEM = "beacon-event-active-nav-item-change", o.DRILLDOWN = "beacon-event-drilldown", o.DRILLUP = "beacon-event-drillup", o.HEADER_ZONE_CLICK = "beacon-event-navigation-header-zone-click", o.COUNTRY_SELECTOR_CLOSE = "beacon-event-country-selector-close", o.COUNTRY_SELECTOR_OPEN = "beacon-event-country-selector-open", o.COUNTRY_FLAG_CHANGE = "beacon-event-country-flag-change", o.HAS_CURRENT_LINK = "beacon-event-has-current-link", o.REGISTER = "beacon-event-register", o.SKIP_LINK_CLICK = "beacon-event-skip-link-click", o.METRICS_SECTION_CHANGE = "beacon-event-metrics-section-change", o.METRICS_LOCATION_CHANGE = "beacon-event-metrics-location-change", o.METRICS_ID_CHANGE = "beacon-event-metrics-id-change", o.NAVIGATION_LINK_CLICK = "beacon-event-navigation-link-click", o))(_ || {}), tt = /* @__PURE__ */ ((o) => (o.CLOSE = "close", o.KEYDOWN = "keydown", o.SCROLL = "scroll", o.CLICK = "click", o.FOCUSOUT = "focusout", o))(tt || {});
const rt = H`[beacon-tag-name]:not([focus-override]) *:focus-visible:not(a){--heritage-width-border-normal: calc(var(--beacon-unit-base) / 8);--heritage-width-border-jumbo: calc(var(--beacon-unit-base) / 4);--heritage-width-border-maxi: calc(var(--beacon-unit-base) / 2);--total-ring-width: calc(var(--beacon-system-width-border-jumbo, var(--heritage-width-border-jumbo)) + var(--beacon-system-width-border-maxi, var(--heritage-width-border-maxi)) + var(--beacon-system-width-border-normal, var(--heritage-width-border-normal)));box-shadow:0 0 0 var(--beacon-system-width-border-jumbo, var(--heritage-width-border-jumbo)) var(--beacon-system-color-border-focus-middle, var(--beacon-color-outline-focus)),0 0 0 var(--beacon-system-width-border-maxi, var(--heritage-width-border-maxi)) var(--beacon-system-color-border-focus-outer, var(--beacon-color-shadow-focus));outline:var(--beacon-system-width-border-normal, var(--heritage-width-border-normal)) solid var(--beacon-system-color-border-focus-inner, var(--beacon-color-border-focus));outline-offset:0;position:relative;z-index:1}[beacon-tag-name]:not([focus-override]) a:focus-visible{--heritage-width-border-normal: calc(var(--beacon-unit-base) / 8);--heritage-width-border-jumbo: calc(var(--beacon-unit-base) / 4);--heritage-width-border-maxi: calc(var(--beacon-unit-base) / 2);--total-ring-width: calc(var(--beacon-system-width-border-jumbo, var(--heritage-width-border-jumbo)) + var(--beacon-system-width-border-maxi, var(--heritage-width-border-maxi)) + var(--beacon-system-width-border-normal, var(--heritage-width-border-normal)));background-color:var(--beacon-system-color-background-link-focus, var(--beacon-color-background-link-focus))!important;border-block-end-color:unset;box-shadow:0 var(--beacon-system-width-border-jumbo, var(--heritage-width-border-jumbo)) 0 0 var(--beacon-system-color-border-link-focus, var(--beacon-color-border-link-focus));color:var(--beacon-system-color-text-set-navy, var(--beacon-color-text-set-navy))!important;outline:none;text-decoration:none}@media (forced-colors: active){[beacon-tag-name]:not([focus-override]) a:focus-visible{border-block-end:var(--beacon-system-width-border-jumbo, var(--heritage-width-border-jumbo)) solid var(--beacon-system-color-border-link-focus, var(--beacon-color-border-link-focus))}}`, it = H`:host(:not([focus-override])) *:not(a):focus-visible{--heritage-width-border-normal: calc(var(--beacon-unit-base) / 8);--heritage-width-border-jumbo: calc(var(--beacon-unit-base) / 4);--heritage-width-border-maxi: calc(var(--beacon-unit-base) / 2);--total-ring-width: calc(var(--beacon-system-width-border-jumbo, var(--heritage-width-border-jumbo)) + var(--beacon-system-width-border-maxi, var(--heritage-width-border-maxi)) + var(--beacon-system-width-border-normal, var(--heritage-width-border-normal)));box-shadow:0 0 0 var(--beacon-system-width-border-jumbo, var(--heritage-width-border-jumbo)) var(--beacon-system-color-border-focus-middle, var(--beacon-color-outline-focus)),0 0 0 var(--beacon-system-width-border-maxi, var(--heritage-width-border-maxi)) var(--beacon-system-color-border-focus-outer, var(--beacon-color-shadow-focus));outline:var(--beacon-system-width-border-normal, var(--heritage-width-border-normal)) solid var(--beacon-system-color-border-focus-inner, var(--beacon-color-border-focus));outline-offset:0;position:relative;z-index:1}:host(:not([focus-override])) a:focus-visible{--heritage-width-border-normal: calc(var(--beacon-unit-base) / 8);--heritage-width-border-jumbo: calc(var(--beacon-unit-base) / 4);--heritage-width-border-maxi: calc(var(--beacon-unit-base) / 2);--total-ring-width: calc(var(--beacon-system-width-border-jumbo, var(--heritage-width-border-jumbo)) + var(--beacon-system-width-border-maxi, var(--heritage-width-border-maxi)) + var(--beacon-system-width-border-normal, var(--heritage-width-border-normal)));background-color:var(--beacon-system-color-background-link-focus, var(--beacon-color-background-link-focus))!important;border-block-end-color:unset;box-shadow:0 var(--beacon-system-width-border-jumbo, var(--heritage-width-border-jumbo)) 0 0 var(--beacon-system-color-border-link-focus, var(--beacon-color-border-link-focus));color:var(--beacon-system-color-text-set-navy, var(--beacon-color-text-set-navy))!important;outline:none;text-decoration:none}@media (forced-colors: active){:host(:not([focus-override])) a:focus-visible{border-block-end:var(--beacon-system-width-border-jumbo, var(--heritage-width-border-jumbo)) solid var(--beacon-system-color-border-link-focus, var(--beacon-color-border-link-focus))}}`, ot = H`[beacon-tag-name]:where([reset-spacing]) *{margin:0;padding:0}`, st = H`:host{border:initial;box-sizing:border-box;display:block;font-size:initial;font-style:initial;font-variant:initial;font-weight:initial;line-height:initial;white-space:initial;word-break:break-word;word-spacing:initial;text-align:initial;text-indent:initial;text-transform:initial}:host *,:host *:before,:host *:after{box-sizing:inherit}input,button,textarea,select{font:inherit;letter-spacing:inherit}button{color:inherit;cursor:pointer}ul,ol{list-style-position:initial;list-style-type:revert}table{caption-side:initial;empty-cells:initial;border-collapse:initial;border-spacing:initial}`, nt = H`.has-cell-padding{padding-block:var(--beacon-cell-space-padding);padding-inline:var(--beacon-cell-space-padding)}.has-component-padding{padding-block:var(--beacon-component-space-padding);padding-inline:var(--beacon-component-space-padding)}@media (width >= 1600px){:host{--beacon-space-default: calc(10 * var(--beacon-unit-base)) !important;--beacon-grid-layout-space-margin: 10rem !important;--beacon-grid-gutter-size-inline: 2rem !important}}:host{color:var(--beacon-system-color-text-standard, var(--beacon-color-text-default));font-family:var(--beacon-system-font-family, var(--beacon-font-family-default));-webkit-font-smoothing:antialiased}[lang=zh]{font-family:var(--beacon-system-font-family, var(--beacon-font-family-cjk))}a,::slotted(a){color:var(--beacon-system-color-text-link, var(--beacon-color-hyperlink-default))}a:not(:is(:hover,:focus)){text-decoration:none}:host(:not([motion-state=enabled])),:host(:not([motion-state=enabled])) :is(*,*:before,*:after),:host(:not([motion-state=enabled])) ::slotted(*:not([motion-state=enabled])){animation:none;animation-duration:.01ms!important;animation-iteration-count:1!important;scroll-behavior:auto!important;transition:none!important}@media (prefers-reduced-motion: reduce){:host([motion-state=enabled]),:host([motion-state=enabled]) :is(*,*:before,*:after),:host([motion-state]),:host([motion-state]) :is(*,*:before,*:after){animation:none;animation-duration:.01ms!important;animation-iteration-count:1!important;scroll-behavior:auto!important;transition:none!important}}`;
var W = /* @__PURE__ */ ((o) => (o.CLICKABLE = "data-metrics-clickable", o.TYPE = "data-metrics-type", o.TEXT = "data-metrics-link-text", o.LOCATION = "data-metrics-location", o))(W || {});
const w = class w {
};
w.setPropertyOnEvent = (e, t, r, i) => {
  try {
    Object.defineProperty(e, r, {
      value: i || t[`${r}`],
      writable: !1
    });
  } catch {
  }
}, w.getAnalyticsEvents = () => {
  const e = ["gesture", "click", "touch", "change"];
  return Object.keys(window).filter((t) => /^on/.test(t) && new RegExp(e.join("|")).test(t));
}, w.setHostEventHandlerForAnalytics = (e, t) => {
  var n;
  const r = w.getAnalyticsEvents(), i = "beaconVersion";
  for (const s of r) {
    const c = s.slice(2);
    (n = e.shadowRoot) == null || n.addEventListener(
      c,
      (a) => {
        var d;
        const l = new a.constructor(c, a);
        w.setPropertyOnEvent(l, null, i, t);
        for (const h in a)
          w.setPropertyOnEvent(l, a, h);
        (d = e.shadowRoot) != null && d.contains(a.target) && e.dispatchEvent(l);
      },
      !0
    );
  }
};
let B = w;
function ft(o, e) {
  !o || !e || o instanceof SVGElement && (o.setAttribute(W.CLICKABLE, "true"), o.setAttribute(W.TYPE, "BTN"), o.setAttribute(W.TEXT, e));
}
function z(o, e, t, r) {
  const i = new CustomEvent(e, {
    bubbles: !0,
    cancelable: !1,
    composed: !0,
    detail: {},
    ...t
  });
  return o.dispatchEvent(i), i;
}
const me = (o) => !!o.deferrable, at = (o) => {
  var e, t;
  return (!!((e = o.deferList) != null && e.includes(x.VISIBLE)) || !!((t = o.getAttribute("defer-until")) != null && t.includes(x.VISIBLE))) && o.isDeferUntilParent;
}, ge = (o) => !!navigator.vendor && navigator.vendor.indexOf(o) > -1;
function ct(o) {
  return `${o}`.toLowerCase().replace(new RegExp(/[-_]+/, "g"), " ").replace(new RegExp(/[^\w\s]/, "g"), "").replace(new RegExp(/\s+(.)(\w*)/, "g"), (e, t, r) => `${t.toUpperCase() + r}`).replace(new RegExp(/\w/), (e) => e.toUpperCase());
}
const lt = "3.3.0", ht = {
  rootMargin: "1500px",
  threshold: 0.1
}, g = {
  version: lt,
  hidden: "hidden",
  safari: "Apple",
  divTagName: "div",
  deprecatedPropertyKeys: ["alternateAttributes", "sync", "alternateSlot"],
  defaultDeferOptions: ht
}, Q = (o) => {
  for (const e of o)
    if (e !== void 0)
      return e;
};
var dt = Object.defineProperty, f = (o, e, t, r) => {
  for (var i = void 0, n = o.length - 1, s; n >= 0; n--)
    (s = o[n]) && (i = s(e, t, i) || i);
  return i && dt(e, t, i), i;
};
const v = class v extends P {
  constructor() {
    super(...arguments), this.isDeferUntilParent = !1, this.deferrable = !0, this.focusOverride = !1, this.resetSpacing = !1, this.beaconTagName = "", this.version = g.version, this.motionState = "disabled", this.prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches, this.alternateSlots = /* @__PURE__ */ new Map(), this.onslotchange = (e) => {
      var i;
      const r = (i = e.target.name) != null ? i : "";
      this.slotDeprecationWarn(r);
    }, this.resolvedDeferOptions = {
      root: document,
      rootMargin: g.defaultDeferOptions.rootMargin,
      threshold: g.defaultDeferOptions.threshold
    }, this.deferUntilVisible = () => new Promise((e) => {
      if (!("IntersectionObserver" in window))
        e();
      else {
        const t = new IntersectionObserver((r) => {
          const [i] = r;
          i.isIntersecting && (t.disconnect(), e());
        }, this.getResolvedDeferOptions() || null);
        t.observe(this);
      }
    }), this.deferUntilIdle = () => {
      const e = new Promise((t) => {
        document.readyState !== "complete" ? window.addEventListener("load", () => t(), { once: !0 }) : t();
      });
      return "requestIdleCallback" in window ? Promise.all([
        new Promise((t) => {
          requestIdleCallback(() => {
            t();
          });
        }),
        e
      ]) : e;
    }, this.deferredChildEventHandler = (e) => new Promise((t) => {
      var r;
      e instanceof CustomEvent && ((r = e.detail) != null && r.type) ? (this.deferOptions && this.setDeferOptions(this.deferOptions), this.selectDeferUntilHandler(e.detail.type).then(() => t())) : t();
    }), this.renderLightDOMStyles = (e = `${this.beaconTagName}-light-DOM-styles`) => {
      const t = [], r = new Set(this.constructor.lightDOMStyles.flat(1 / 0));
      for (const i of r)
        t.push(i);
      t.reverse().forEach((i, n) => {
        var a;
        const s = document.createElement("style");
        s.setAttribute("slot", "lightDOM");
        let c = !1;
        if (s.textContent = i.toString(), e) {
          const l = Math.abs(n - t.length), d = `${e}-${l}`, h = this.getClosestHost();
          s.id = d, (h && ((a = h == null ? void 0 : h.shadowRoot) != null && a.querySelector(`#${d}`)) || !h && document.querySelector(`#${d}`)) && (c = !0);
        }
        c || this.prepend(s);
      });
    }, this.renderComponentModeStyles = (e = `${this.beaconTagName}-mode-styles`) => {
      var t;
      (t = this.componentModeStyles) == null || t.forEach((r, i) => {
        const n = document.createElement("style");
        n.textContent = r.toString();
        const s = `${e}-${i}`;
        n.id = s, !document.querySelector(`#${s}`) && document.head.append(n);
      });
    }, this.getScopedTag = (e) => {
      if (!e)
        return null;
      const t = this.getScopedTagPrefix() ? `${this.getScopedTagPrefix()}-${e}` : e;
      return Be(t);
    };
  }
  /**
   * Extends Lit's definition of `observedAttributes`.
   *
   * Returns a list of attributes corresponding to the registered properties.
   * @nocollapse
   * @category attributes
   */
  static get observedAttributes() {
    const e = [];
    return this.hasDeprecatedProperties && this.alternateAttributesMap.forEach((t, r) => {
      this.elementProperties.has(t) && e.push(r);
    }), e.push(...super.observedAttributes), e;
  }
  /**
   * Extends Lit's default attributeChangedCallback to support deprecated properties.
   *
   * @param name Attribute name
   * @param _old Old value
   * @param value New value
   */
  attributeChangedCallback(e, t, r) {
    const { alternateAttributesMap: i, hasDeprecatedProperties: n, elementProperties: s } = this.constructor;
    if (n && i.size > 0) {
      const c = i.get(e), a = s.get(c), l = this.getPropertyOptions(e).alternateSlot;
      if (c)
        if (c !== F.NO_SYNC && (r === null ? this.removeAttribute((a == null ? void 0 : a.attribute) || c) : this.setAttribute((a == null ? void 0 : a.attribute) || c, r || "")), l) {
          const d = this.createPropertyDeprecationForSlotMessage(e, l);
          console.warn(d);
        } else
          console.warn(this.createPropertyDeprecationMessage(e, c, a == null ? void 0 : a.attribute));
    }
    super.attributeChangedCallback(e, t, r);
  }
  /**
   * Generates a console message to log when deprecated properties were used.
   *
   * @param name attribute that triggered the message
   * @param property property that the attribute aligns to
   * @param attribute correct attribute to use
   */
  createPropertyDeprecationMessage(e, t, r) {
    let i = `Beacon Warning: [${e}] will be deprecated as a prop of <${this.beaconTagName.toLowerCase()}> in a future release.`;
    return t !== F.NO_SYNC && (i += ` Start using [${r || t}] instead to avoid future disruptions.`), i;
  }
  /**
   * Generates a console message to log when a deprecated property is used
   * and should instead use the designated slot
   * @param property the name of the property being deprecated
   * @param slot the designated slot to use instead
   */
  createPropertyDeprecationForSlotMessage(e, t) {
    return `Beacon Warning: '${e}' will be deprecated as a prop of <${this.beaconTagName.toLowerCase()}> in a future release. Start using the slot: '${t}' instead to avoid future disruptions`;
  }
  /**
   * Extend's Lit's `createProperty` method for our own uses.
   * @param name Property Name
   * @param options Options for the property
   */
  static createProperty(e, t) {
    if (t) {
      const { alternateAttributes: r, attribute: i } = t, n = Object.prototype.hasOwnProperty.call(t, "sync");
      if (g.deprecatedPropertyKeys.forEach((s) => {
        Object.prototype.hasOwnProperty.call(t, s) && (this.hasDeprecatedProperties = !0);
      }), r && r.forEach((s) => {
        this.alternateAttributesMap.set(s, e);
      }), n && !t.sync) {
        const s = i != null ? i : e;
        this.alternateAttributesMap.set(s, F.NO_SYNC);
      }
    }
    super.createProperty(e, t);
  }
  /**
   * This is used to return the property options of a property when given only its attribute name,
   * e.g. the property pageTitle with an attribute name of 'page-title' will have no way of accessing its options
   * directly when attempting to grab the options with 'page-title' and elementProperties.get().
   * @param name the resolved attribute name of a `@property()` decorated property
   * @returns the options passed in `@property()` e.g. `{attribute: 'attribute-name', reflect: true}`
   */
  getPropertyOptions(e) {
    var r;
    const { elementProperties: t } = this.constructor;
    return (r = t.get(e)) != null ? r : [...t.values()].find((i) => i.attribute === e);
  }
  /**
   * Getter that returns private resolvedDeferOptions
   * @returns resolvedDeferOptions that are passed to an IntersectionObserver
   */
  getResolvedDeferOptions() {
    return this.resolvedDeferOptions;
  }
  /**
   * Converts space separated list prop of `defer-until` to a DeferUntil enum array
   */
  get deferList() {
    var e, t;
    return (t = (e = this.deferUntil) == null ? void 0 : e.split(" ").map((r) => r).filter((r) => !!r)) != null ? t : [];
  }
  /**
   * Logs a warning based on the slot name provided and details from the alternate slots map
   *
   * @param slotName name of the slot that has changed
   */
  slotDeprecationWarn(e) {
    const t = this.alternateSlots.get(e);
    if (t !== void 0) {
      let r;
      const i = `The '${e}' for for '${this.beaconTagName}'`;
      Object.keys(t).some((n) => n.match("alternate")) ? t.alternateAttribute ? r = `will be replaced by the element property '${t.alternateAttribute}'` : r = `will be replaced by the '${t.alternateSlots}' slot` : r = "will be removed", console.warn(`Beacon Deprecation Warning: ${i} ${r} in foundation@${t.version}`);
    }
  }
  /**
   * A setter that takes user input deferOptions and resolves them compared to existing deferOptions. Enables the setting
   * of isolated deferOptions and leaves unchanged options previously set intact.
   * @param options the defer-options object used in IntersectionObserver.
   */
  setDeferOptions(e) {
    if (!e)
      return;
    const t = { ...this.resolvedDeferOptions };
    this.resolvedDeferOptions = {
      root: Q([e == null ? void 0 : e.root, t == null ? void 0 : t.root, document]),
      rootMargin: Q([
        e == null ? void 0 : e.rootMargin,
        t == null ? void 0 : t.rootMargin,
        g.defaultDeferOptions.rootMargin
      ]),
      threshold: Q([
        e == null ? void 0 : e.threshold,
        t == null ? void 0 : t.threshold,
        g.defaultDeferOptions.threshold
      ])
    };
  }
  /**
   * Hides the visibility of the component. Used when component is deferred until specified condition.
   * @returns void
   */
  setDeferVisibilityStyles() {
    ge(g.safari) || (this.style.visibility = g.hidden);
  }
  /**
   * Sets the component styles to visibile. Used when component meets condition specified to defer until.
   * @returns void
   */
  removeDeferVisibilityStyles() {
    ge(g.safari) || this.style.removeProperty("visibility");
  }
  /**
   * Searches for a parent of component with defer-until attribute that is not solely defer-until="defined".
   * @returns closest lightDOM parent with [defer-until] attribute or null
   */
  getClosestDeferredParent() {
    const e = '[defer-until]:not([defer-until="defined"])', t = this.closest(e);
    if (t && me(t))
      return t;
    const r = this.getClosestHost();
    if (r) {
      const i = r.closest(e);
      if (i && me(i))
        return i;
    }
    return null;
  }
  /**
   * Runs in shadowDOM context. This method searches for the ShadowRoot then returns host of ShadowRoot.
   * @returns host element of this component of the shadowDOM
   */
  getClosestHost() {
    let e = this && this.parentNode;
    for (; e; ) {
      if (e instanceof ShadowRoot)
        return e.host;
      e = e.parentNode;
    }
    return null;
  }
  /**
   * Selects which deferUntil handler method to run on component.
   * @param deferUntilCriteria DeferUntil option that determines how component should defer.
   */
  selectDeferUntilHandler(e) {
    return new Promise((t) => {
      e.includes(x.VISIBLE) ? (this.setDeferVisibilityStyles(), this.deferUntilVisible().then(() => {
        this.removeDeferVisibilityStyles(), t();
      })) : e.includes(x.IDLE) ? (this.setDeferVisibilityStyles(), this.deferUntilIdle().then(() => {
        this.removeDeferVisibilityStyles(), t();
      })) : t();
    });
  }
  /**
   * Method that handles setup of deferUntil handler when deferUntil is configured locally via deferUntil prop on the component.
   */
  handleLocalDeferUntil() {
    return new Promise((e) => {
      this.deferOptions && this.setDeferOptions(this.deferOptions), this.selectDeferUntilHandler(this.deferList).then(() => e());
    });
  }
  /**
   * Method that looks for the closest element to the component with 'defer-until' attribute and either
   * applies the parent's deferUntil logic to the component immediately, if the parent is already registered as `isDeferUntilParent`,
   * or adds an eventListener to toggle parent's deferUntil logic on component once parent has emitted the beacon-event-deferred-child Event.
   * @param closestDeferredParent is the closest element to the component that has the 'defer-until' attribute set.
   */
  handleParentalDeferUntil(e) {
    return new Promise((t) => {
      if (e === null)
        t();
      else if (at(e))
        this.selectDeferUntilHandler(e.deferList).then(() => t());
      else {
        const r = (i) => {
          this.deferredChildEventHandler(i).then(() => {
            e.removeEventListener(_.DEFERRED_CHILD, r), t();
          });
        };
        e.addEventListener(_.DEFERRED_CHILD, r);
      }
    });
  }
  /**
   * This method handles defer logic. If component has deferUntil prop set, it calls method to handle deferUntil logic locally.
   * Else if component has a closest element with 'defer-until' attribute set, it handles deferUntil based on closestDeferredParent's deferUntil settings.
   * If neither of the above criteria match, it completes connectedCallback.
   */
  handleDefer() {
    return new Promise((e) => {
      if (!this.deferrable || this.motionState === "enabled")
        e();
      else if (this.deferUntil)
        this.registerAsDeferUntilParent(), this.handleLocalDeferUntil().then(() => e());
      else {
        const t = this.getClosestDeferredParent();
        t ? this.handleParentalDeferUntil(t).then(() => e()) : e();
      }
    });
  }
  /**
   * Method that registers a component as parental component that can trigger deferUntil logic in child components. It should be placed in connectedCallback
   * of all components that need to trigger deferUntil logic in child components.
   */
  registerAsDeferUntilParent() {
    if (this.deferOptions && this.setDeferOptions(this.deferOptions), this.deferList.includes(x.VISIBLE)) {
      const e = new IntersectionObserver((t) => {
        for (const r of t)
          r.target && (this.isDeferUntilParent = !0, this.emitDeferredChild(), e.disconnect());
      }, this.getResolvedDeferOptions() || null);
      e.observe(this);
    }
  }
  /**
   * @returns the scope prefix from the beginning of the tagName.
   */
  getScopedTagPrefix() {
    const e = this.tagName.toLowerCase().split("-beacon")[0];
    return e.startsWith("beacon") ? "" : e;
  }
  /**
   * Defines this web component in the CustomElementRegistry.
   * @param tagNamePrefix lowercase string that prefixes default component tag names for scoping purposes
   */
  static registerComponent(e) {
    if (this.tagName) {
      const t = `${e ? `${e}-` : ""}${this.tagName}`;
      if (!customElements.get(t)) {
        const r = class extends this {
        };
        Object.defineProperty(r, "name", { value: ct(t) }), customElements.define(t, r), customElements.whenDefined(t).then(() => {
          const i = new CustomEvent(_.REGISTER, { detail: t });
          document.dispatchEvent(i);
        }).catch((i) => {
          console.error(`Error defining Beacon Component ${t} in CustomElementsRegistry: `, i);
        });
      }
    }
  }
  /**
   * This will return beacon tag name to scoped tag Name
   * @param tag beaconTagName
   * @returns Scoped tagName
   */
  getScopedTagName(e) {
    return e ? this.getScopedTagPrefix() ? `${this.getScopedTagPrefix()}-${e}` : e : "";
  }
  /**
   * @param event
   * @event {BeaconEvents} beacon-event-click - emits when clicked
   */
  emitClick(e) {
    z(this, _.CLICK, !!this.onBeaconClick && this.onBeaconClick(e));
  }
  /**
   * @param event
   * @event {BeaconEvents} beacon-event-keydown - emits when a keydown event would be fired
   */
  emitKeydown(e, t) {
    z(this, _.KEYDOWN, !!this.onBeaconKeydown && this.onBeaconKeydown(t));
  }
  /**
   * Emitter of beacon-event-deferred-child event used by components registered as deferUntilParents.
   * @param event
   * @event {BeaconEvents} beacon-event-deferred-child - signals to children of a deferUntil parent that they should behave as deferUntil components
   */
  emitDeferredChild(e) {
    z(this, _.DEFERRED_CHILD, {
      detail: {
        element: this,
        type: this.deferList
      }
    });
  }
  /**
   * Emits a beacon-event-keydown event as a default to mousedown handling
   * @param event
   */
  handleKeydown(e) {
    this.emitKeydown(e);
  }
  /**
   * Check if the component has a specific mixin applied.
   * @param name Name of the mixin to look for.
   * @returns True if the class contains that mixin.
   */
  hasMixin(e) {
    var t;
    try {
      if (!((t = this.constructor.mixins) == null ? void 0 : t.includes(e)))
        throw new Error(`This class does not implement ${e}.`);
      return !0;
    } catch (r) {
      return console.error(r), !1;
    }
  }
  /**
   * Check for a field with a specific value on the class.
   *
   * Useful when a mixin relies on the existance of another mixin.
   *
   * @param field Class property to check for on the mixin.
   * @param value Value of the given class field to check for.
   * @returns True if the value of the field is the same.
   */
  hasFieldAndValue(e, t) {
    try {
      if (!(e in this))
        throw new Error(`${e} does not exist on this: ${this}`);
      return this[e] === t;
    } catch (r) {
      return console.error(r), !1;
    }
  }
  /**
   * Placeholder method that collects all method functionality that is decorated with @prerender
   * and is called in connectedCallback. This method should not be overridden directly.
   * The goal is to prevent direct overrides of connectedCallback in subclasses
   */
  prerender() {
  }
  /**
   * Placeholder method that collects all method functionality that is decorated with @postrender
   * and is called in firstUpdated. This method should not be overridden directly.
   * The goal is to prevent direct overrides of firstUpdated in subclasses
   */
  postrender() {
  }
  /**
   * Placeholder method that collects all method functionality that is decorated with @destroy
   * and is called in disconnectedCallback. This method should not be overridden directly.
   * The goal is to prevent direct overrides of disconnectedCallback in subclasses
   */
  destroy() {
  }
  /**
   * Enables or disables motion on this component.
   *
   * Looks for the closest element with `[motion-state]` to find its value and set the motion state of the component equal to that.
   *
   * @link https://developer.mozilla.org/en-US/docs/Web/API/Element/closest Element.closest() will return the element itself if it matches the selector.
   */
  setMotionState() {
    if (this.motionState === "enabled")
      return;
    const e = this.closest("[motion-state]"), t = e == null ? void 0 : e.getAttribute("motion-state");
    t && (t === "enabled" || t === "disabled") && (this.motionState = t);
  }
  connectedCallback() {
    this.setMotionState(), this.handleDefer().then(() => {
      super.connectedCallback(), B.setHostEventHandlerForAnalytics(this, this.version), this.renderLightDOMStyles(), this.prerender();
    });
  }
  firstUpdated() {
    this.postrender(), z(this, _.ELEMENT_READY);
  }
  disconnectedCallback() {
    super.disconnectedCallback(), this.destroy();
  }
};
v.styles = [st, nt, it], v.tagName = "", v.shadowRootOptions = { ...P.shadowRootOptions, delegatesFocus: !0 }, v.alternateAttributesMap = /* @__PURE__ */ new Map(), v.hasDeprecatedProperties = !1, v.mixins = [], v.lightDOMStyles = [ot, rt];
let p = v;
f([
  b({ attribute: "focus-override", type: Boolean, reflect: !0 })
], p.prototype, "focusOverride");
f([
  b({ attribute: "reset-spacing", type: Boolean, reflect: !0 })
], p.prototype, "resetSpacing");
f([
  b({ attribute: "beacon-tag-name", type: String, reflect: !0 })
], p.prototype, "beaconTagName");
f([
  b({ attribute: "beacon-version", reflect: !0 })
], p.prototype, "version");
f([
  b({ attribute: "defer-until" })
], p.prototype, "deferUntil");
f([
  b({ attribute: "defer-options" })
], p.prototype, "deferOptions");
f([
  b({ type: Function })
], p.prototype, "onBeaconClick");
f([
  b({ type: Function })
], p.prototype, "onBeaconKeydown");
f([
  b({ attribute: "motion-state", reflect: !0 })
], p.prototype, "motionState");
f([
  Ye()
], p.prototype, "prefersReducedMotion");
export {
  W as A,
  p as B,
  tt as E,
  u as T,
  bt as a,
  _ as b,
  ft as c,
  Be as d,
  z as e,
  H as i,
  Je as j,
  b as n,
  Ye as r,
  T as w,
  Ze as x,
  pt as z
};
